import { asList, hasProduct } from "@/engine/answers";
import { licenseQuestionKey, loginQuestionKey, networkHintKey, visibleQuestionIds } from "@/engine/questions";
import type { WizardAnswers } from "@/types";

export type ProgressSegmentState = "past" | "current" | "future";

export interface ProgressSegment {
  id: string;
  state: ProgressSegmentState;
  tooltip?: string;
}

export interface QuestionContext {
  kind: "dual" | "single";
  products: Array<{ id: string; label: string }>;
}

const RW_PLATFORM_OPTIONS = [
  { value: "android", label: "Android", icons: ["android"] as const },
  { value: "chrome_edge", label: "Chrome/Edge", icons: ["chrome", "edge"] as const },
  { value: "ios", label: "iOS", icons: ["ios"] as const },
  { value: "mac", label: "Mac", icons: ["macos"] as const },
  { value: "win", label: "Windows", icons: ["windows"] as const },
];

const MINDVIEW_PLATFORM_OPTIONS = [
  { value: "assist", label: "Assist", icons: ["mindview_assist"] as const },
  { value: "mac", label: "Mac", icons: ["macos"] as const },
  { value: "online", label: "Online", icons: ["mindview_online"] as const },
  { value: "win", label: "Windows", icons: ["windows"] as const },
];

const MINDVIEW_ASSIST_PLATFORM_OPTIONS = [
  { value: "android", label: "Android", icons: ["android"] as const },
  { value: "ios", label: "iOS", icons: ["ios"] as const },
];

const TASK_PLATFORM_OPTIONS = [
  { value: "chromeos", label: "Browser", icons: ["browser"] as const },
  { value: "mac", label: "Mac", icons: ["macos"] as const },
  { value: "win", label: "Windows", icons: ["windows"] as const },
];

type PlatformOption =
  | (typeof RW_PLATFORM_OPTIONS)[number]
  | (typeof MINDVIEW_PLATFORM_OPTIONS)[number]
  | (typeof MINDVIEW_ASSIST_PLATFORM_OPTIONS)[number]
  | (typeof TASK_PLATFORM_OPTIONS)[number];

function selectedPlatforms(
  options: readonly PlatformOption[],
  selected: string[],
  applicable: string[] = selected,
): PlatformOption[] {
  return options.filter(
    (option) => selected.includes(option.value) && applicable.includes(option.value),
  );
}

function uniquePlatforms(platforms: PlatformOption[]): PlatformOption[] {
  return platforms
    .filter(
      (option, index, all) => all.findIndex((candidate) => candidate.label === option.label) === index,
    )
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function buildProgressSegments(
  step: string,
  answers: WizardAnswers,
  formatAnswer: (id: string) => string,
  customerTypeLabel?: string,
): ProgressSegment[] {
  const visible = visibleQuestionIds(answers);
  const isReview = step === "review";
  const currentIndex = isReview ? visible.length : visible.indexOf(step);

  const segments: ProgressSegment[] = [
    {
      id: "welcome",
      state: "past",
      tooltip: customerTypeLabel,
    },
  ];

  for (let index = 0; index < visible.length; index++) {
    const id = visible[index];
    let state: ProgressSegmentState = "future";
    if (isReview) {
      state = "past";
    } else if (index === currentIndex) {
      state = "current";
    } else if (index < currentIndex) {
      state = "past";
    }
    segments.push({
      id,
      state,
      tooltip: state === "past" ? formatAnswer(id) : undefined,
    });
  }

  segments.push({
    id: "review",
    state: isReview ? "current" : "future",
  });

  return segments;
}

export function progressPosition(
  step: string,
  visible: string[],
): { current: number; total: number } {
  const total = visible.length + 2;
  if (step === "review") {
    return { current: total - 1, total };
  }
  const currentIndex = visible.indexOf(step);
  return {
    current: currentIndex >= 0 ? currentIndex + 1 : 1,
    total,
  };
}

export function questionContext(step: string, answers: WizardAnswers): QuestionContext | null {
  if (step === "products" || step === "review") return null;

  const products = asList(answers.products);
  const rwVersions = asList(answers.rw_version);
  const rwDesktop = rwVersions.includes("win") || rwVersions.includes("mac");

  const networkProducts = [
    products.includes("rw") &&
      (rwVersions.includes("chrome_edge") ||
        ((rwVersions.includes("win") || rwVersions.includes("mac")) && answers.offline !== "yes"))
      ? { id: "rw", label: "Read&Write for Work" }
      : undefined,
    products.includes("mindview") ? { id: "mindview", label: "MindView" } : undefined,
    products.includes("tasks") && asList(answers.tasks_os).length > 0
      ? { id: "tasks", label: "Tasks" }
      : undefined,
  ].filter((product): product is { id: string; label: string } => Boolean(product));

  if (step === "network_restricted" && networkProducts.length > 1) {
    return {
      kind: "dual",
      products: networkProducts,
    };
  }

  if (
    products.includes("rw") &&
    products.includes("mindview") &&
    ((step === "login_method" && (rwDesktop || rwVersions.includes("chrome_edge"))) ||
      step === "rw_license")
  ) {
    return {
      kind: "dual",
      products: [
        { id: "rw", label: "Read&Write for Work" },
        { id: "mindview", label: "MindView" },
      ],
    };
  }

  const productId =
    step === "tasks_os" ||
    step.startsWith("tasks_") ||
    (products.includes("tasks") && !products.includes("rw") && !products.includes("mindview") && step === "network_restricted")
      ? "tasks"
      : step === "mv_version" ||
          step.startsWith("mv_") ||
          (products.includes("mindview") &&
            !products.includes("rw") &&
            step === "rw_license") ||
          (products.includes("mindview") &&
            (!products.includes("rw") ||
              (step === "network_restricted" && answers.offline === "yes")) &&
            ["network_restricted", "login_method", "mv_win_restrict_online_services"].includes(step))
        ? "mindview"
        : "rw";

  const label =
    productId === "tasks" ? "Tasks" : productId === "mindview" ? "MindView" : "Read&Write for Work";

  return { kind: "single", products: [{ id: productId, label }] };
}

export function stepHint(step: string, answers: WizardAnswers): string | undefined {
  if (step === "offline") return "wizard.steps.offline.hint";
  if (step === "tasks_intune") return "wizard.steps.tasks_intune.hint";
  if (step === "network_restricted") return networkHintKey(answers);
  return undefined;
}

export function questionKey(step: string, answers: WizardAnswers): string {
  if (step === "rw_license") return `wizard.steps.rw_license.${licenseQuestionKey(answers)}`;
  if (step === "login_method") return `wizard.steps.login_method.${loginQuestionKey(answers)}`;
  return `wizard.steps.${step}.question`;
}

export function appliesToPlatforms(step: string, answers: WizardAnswers) {
  const rwVersions = asList(answers.rw_version);
  const mindViewVersions = asList(answers.mv_version);
  const mindViewAssistPlatforms = asList(answers.mv_assist_platforms);
  const tasksPlatforms = asList(answers.tasks_os);

  if (
    ["rw_license", "rw_version", "mv_version", "mv_assist_platforms", "tasks_os"].includes(
      step,
    )
  ) {
    return [];
  }

  if (["tasks_intune", "tasks_start_menu"].includes(step)) {
    return TASK_PLATFORM_OPTIONS.filter((option) => option.value === "win");
  }

  if (step === "tasks_browser") {
    return TASK_PLATFORM_OPTIONS.filter((option) => option.value === "chromeos");
  }

  if (step === "offline") {
    return selectedPlatforms(RW_PLATFORM_OPTIONS, rwVersions, ["win", "mac"]);
  }

  if (step === "use_on_webpages") {
    return RW_PLATFORM_OPTIONS.filter((option) => option.value === "mac");
  }

  if (step === "network_restricted") {
    const rwPlatforms = hasProduct(answers, "rw")
      ? selectedPlatforms(
          RW_PLATFORM_OPTIONS,
          rwVersions,
          answers.offline === "yes" ? ["chrome_edge", "ios"] : ["win", "mac", "chrome_edge", "ios"],
        )
      : [];
    const mindViewPlatforms = hasProduct(answers, "mindview")
      ? selectedPlatforms(MINDVIEW_PLATFORM_OPTIONS, mindViewVersions)
      : [];
    const mindViewAssistPlatformsForQuestion =
      hasProduct(answers, "mindview") && mindViewVersions.includes("assist")
        ? selectedPlatforms(MINDVIEW_ASSIST_PLATFORM_OPTIONS, mindViewAssistPlatforms)
        : [];
    const selectedTaskPlatforms = hasProduct(answers, "tasks")
      ? selectedPlatforms(TASK_PLATFORM_OPTIONS, tasksPlatforms)
      : [];

    return uniquePlatforms([
      ...rwPlatforms,
      ...mindViewPlatforms,
      ...mindViewAssistPlatformsForQuestion,
      ...selectedTaskPlatforms,
    ]);
  }

  if (step === "login_method") {
    const rwPlatforms = hasProduct(answers, "rw")
      ? selectedPlatforms(
          RW_PLATFORM_OPTIONS,
          rwVersions,
          answers.offline === "yes" ? ["chrome_edge"] : ["win", "mac", "chrome_edge"],
        )
      : [];
    const mindViewPlatforms = hasProduct(answers, "mindview")
      ? selectedPlatforms(MINDVIEW_PLATFORM_OPTIONS, mindViewVersions, ["win", "mac"])
      : [];
    return uniquePlatforms([...rwPlatforms, ...mindViewPlatforms]);
  }

  if (step === "mv_win_restrict_online_services") {
    return selectedPlatforms(MINDVIEW_PLATFORM_OPTIONS, mindViewVersions, ["win", "mac"]);
  }

  return [];
}
